import { useState } from 'react'
import { PictureOutlined } from '../../../components/icons'
import { cx } from '../../../components/ui/cx'

export interface CatalogResultThumbProps {
  /** `sourceable.imageRef` — opaque wire URL/path (ADR-0090 Decision 2);
   * null/undefined (the field is optional on the wire) renders the
   * placeholder. */
  readonly imageRef: string | null | undefined
  /** The part's display name (see `format.ts`'s `resultDisplayName`) — used
   * as the real image's `alt` text. An image of a specific catalog part is
   * INFORMATIVE, not decorative, so it needs a meaningful name, not `alt=""`
   * (accessibility-wcag skill; fixes the pre-UID-33 bug of an always-empty
   * `alt` on a real photo). */
  readonly name: string
  readonly className?: string
}

/**
 * The catalog result thumbnail (UID-33), extracted out of `ResultCard.tsx`
 * as a standalone, reusable piece. Fixes two accessibility bugs the original
 * inline markup had: a real image's `alt` was always `""` (wrong — the image IS the
 * content, not decoration), and the no-image case was an empty
 * `aria-hidden` div with nothing for a screen-reader OR sighted user to key
 * off of. The placeholder now pairs a decorative icon with a real, visible
 * "No image available" caption — icon+text together, never color/icon-only
 * (WCAG 1.4.1).
 *
 * ADR-0090 D2 describes a missing/404ing `imageRef` as "self-healing" to the
 * placeholder, but a bare `<img>` does NOT do that on its own — a failed
 * network load just renders a broken-image glyph forever. `onError` tracks
 * the failure locally and falls back to the same placeholder markup a
 * null/undefined `imageRef` uses, which is what finally makes that asserted
 * failure mode real.
 *
 * The `hasErrored` flag is reset whenever `imageRef` itself changes: a
 * different ref (e.g. this same DOM node reused for a re-sorted or re-fetched
 * row) deserves its own fresh attempt and must never inherit a stale "broken"
 * memory from the previous ref.
 *
 * Merge note (UID-327/328 × main, 2026-08-27): `main` and the UID-327/328
 * branch arrived at this `onError` fallback INDEPENDENTLY, from the same
 * ADR-0090 D2 reading. Resolved to this version because it is a strict
 * superset — same fallback, plus the ref-change reset `main`'s version
 * lacked. See ADR-0112 D3 for the paired api-side fix that stopped
 * the RFQ line thumbnail seed emitting a path with no bytes behind it (that
 * seed was `seedDemoLinePart`; UID-439 deleted its name/part-number half as a
 * defect and kept only the thumbnail pick, now `seedDemoPartThumbnailRef`).
 * (Note ADR-0110
 * is a DIFFERENT, catalog-side ADR — this work renumbered 0110 → 0112 on
 * merging `main`; see the renumber note in `docs/adr/README.md`.)
 */
export function CatalogResultThumb({ imageRef, name, className }: CatalogResultThumbProps) {
  const [hasErrored, setHasErrored] = useState(false)
  const [lastImageRef, setLastImageRef] = useState(imageRef)
  if (imageRef !== lastImageRef) {
    setLastImageRef(imageRef)
    setHasErrored(false)
  }

  if (imageRef && !hasErrored) {
    return (
      <img
        className={cx('catalog-result-image', className)}
        src={imageRef}
        alt={name}
        onError={() => setHasErrored(true)}
      />
    )
  }
  return (
    <div className={cx('catalog-result-image', 'catalog-result-image-placeholder', className)}>
      <PictureOutlined aria-hidden="true" className="catalog-result-image-placeholder-icon" />
      <span className="catalog-result-image-placeholder-caption">No image available</span>
    </div>
  )
}
